import React, { useState, useEffect, useRef, useCallback } from "react";
import { InternalControlData, getDefaultControlData } from "./types";
import { AutomatedControlData, getDefaultAutomatedControlData } from "./types-automated";
import { ControlSummary } from "./sections/ControlSummary";
import { EvaluateDesign } from "./sections/EvaluateDesign";
import { PriorYearAudit } from "./sections/PriorYearAudit";
import { RiskAssociated } from "./sections/RiskAssociated";
import { OperatingEffectiveness } from "./sections/OperatingEffectiveness";
import { Rollforward } from "./sections/Rollforward";
import { ControlSummaryAutomated } from "./sections/ControlSummaryAutomated";
import { EvaluateDesignAutomated } from "./sections/EvaluateDesignAutomated";
import { RiskAssociatedAutomated } from "./sections/RiskAssociatedAutomated";
import { OperatingEffectivenessAutomated } from "./sections/OperatingEffectivenessAutomated";
import { RollforwardAutomated } from "./sections/RollforwardAutomated";
import { Button } from "../ui/button";
import { Save, Loader2, MessageSquare } from "lucide-react";
import { ReviewComment } from "../shared/ReviewComment";

type SectionType =
  | "control-summary"
  | "evaluate-design"
  | "prior-year"
  | "risk-associated"
  | "operating-effectiveness"
  | "rollforward";

interface InternalControlViewProps {
  controlId?: string;
  controlName?: string;
  activeSection?: SectionType;
  onSectionChange?: (section: SectionType) => void;
  onClose?: () => void;
}

export const InternalControlView: React.FC<InternalControlViewProps> = ({
  controlId,
  controlName,
  activeSection: externalActiveSection,
  onSectionChange,
  onClose,
}) => {
  const [internalActiveSection, setInternalActiveSection] = useState<SectionType>("control-summary");
  const [isAutomated, setIsAutomated] = useState<boolean>(false);
  const [manualData, setManualData] = useState<InternalControlData>(() => {
    const defaultData = getDefaultControlData();
    if (controlId) defaultData.controlId = controlId;
    if (controlName) defaultData.controlName = controlName;
    return defaultData;
  });
  const [automatedData, setAutomatedData] = useState<AutomatedControlData>(() => {
    const defaultData = getDefaultAutomatedControlData();
    if (controlId) defaultData.controlId = controlId;
    if (controlName) defaultData.controlName = controlName;
    return defaultData;
  });
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<{ type: "success" | "error" | null; message: string }>({
    type: null,
    message: "",
  });
  const loadedControlIdRef = useRef<string | undefined>(undefined);
  const isLoadingRef = useRef(false);
  const isInitialLoadCompleteRef = useRef(false);
  
  // Review comment state
  const [reviewCommentOpen, setReviewCommentOpen] = useState(false);
  const [reviewCommentSection, setReviewCommentSection] = useState<SectionType | null>(null);

  // Use external activeSection if provided, otherwise use internal state
  const activeSection = externalActiveSection || internalActiveSection;
  
  // Update internal state when external section changes
  useEffect(() => {
    if (externalActiveSection) {
      setInternalActiveSection(externalActiveSection);
    }
  }, [externalActiveSection]);

  const handleManualDataChange = useCallback((updates: Partial<InternalControlData>) => {
    // Prevent updates during initial load to avoid infinite loops
    if (!isInitialLoadCompleteRef.current && isLoadingRef.current) {
      return;
    }
    setManualData((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleAutomatedDataChange = useCallback((updates: Partial<AutomatedControlData>) => {
    // Prevent updates during initial load to avoid infinite loops
    if (!isInitialLoadCompleteRef.current && isLoadingRef.current) {
      return;
    }
    setAutomatedData((prev) => ({ ...prev, ...updates }));
  }, []);

  const handleSectionChange = (section: SectionType) => {
    if (onSectionChange) {
      onSectionChange(section);
    } else {
      setInternalActiveSection(section);
    }
  };

  // Reset refs when controlId changes
  useEffect(() => {
    if (controlId !== loadedControlIdRef.current) {
      loadedControlIdRef.current = undefined;
      isLoadingRef.current = false;
      isInitialLoadCompleteRef.current = false;
    }
  }, [controlId]);

  // Load existing data when controlId changes (only once per controlId)
  useEffect(() => {
    if (!controlId) return;
    
    // Prevent multiple loads for the same controlId
    if (loadedControlIdRef.current === controlId) return;
    if (isLoadingRef.current) return;
    
    // Mark as loading BEFORE async operation
    const currentControlId = controlId;
    loadedControlIdRef.current = currentControlId;
    isLoadingRef.current = true;

    const loadData = async () => {
      // Declare natureFromRcm outside try block so it's accessible in catch
      let natureFromRcm: string | null = null;
      
      try {
        // First, try to load Nature from Libraries_RCM.json
        if (window.sharePointAPI && typeof window.sharePointAPI.downloadRcmJson === "function") {
          try {
            const rcmResult = await window.sharePointAPI.downloadRcmJson();
            console.log(`[InternalControlView] RCM download result for ${currentControlId}:`, rcmResult);
            if (rcmResult.success && rcmResult.data) {
              const librariesRcm = rcmResult.data.libraries_RCM || rcmResult.data.libraries_rcm || {};
              console.log(`[InternalControlView] Libraries_RCM data:`, librariesRcm);
              const controlEntry = librariesRcm[currentControlId];
              console.log(`[InternalControlView] Control entry for ${currentControlId}:`, controlEntry);
              if (controlEntry && controlEntry.Nature) {
                natureFromRcm = controlEntry.Nature;
                console.log(`[InternalControlView] Found Nature from RCM: ${natureFromRcm}`);
              } else {
                console.log(`[InternalControlView] No Nature found in control entry for ${currentControlId}`);
              }
            } else {
              console.log(`[InternalControlView] RCM download failed or no data:`, rcmResult);
            }
          } catch (e) {
            console.error("Failed to load Nature from Libraries_RCM.json:", e);
          }
        } else {
          console.log(`[InternalControlView] downloadRcmJson API not available`);
        }

        const fileName = `Libraries_InternalControlResponses_${currentControlId}.json`;
        if (window.sharePointAPI && typeof window.sharePointAPI.downloadClientJson === "function") {
          const result = await window.sharePointAPI.downloadClientJson(fileName);
          console.log(`[InternalControlView] Response file download result for ${fileName}:`, result);
          // Handle empty file {} or missing data
          if (result.success && result.data && Object.keys(result.data).length > 0 && result.data.InternalControls?.Control) {
            console.log(`[InternalControlView] Found saved data for ${currentControlId}`);
            const controlData = result.data.InternalControls.Control;
            const summary = controlData.ControlSummary || {};
            const details = summary.ControlDetails || {};
            const attributes = summary.ControlAttributes || {};
            
            // Determine if automated based on Nature from RCM (source of truth) or saved data
            // Prioritize natureFromRcm over saved attributes.Nature since RCM is the authoritative source
            const nature = natureFromRcm || attributes.Nature || "Manual";
            const isAutomatedControl = nature === "Automated";
            
            console.log(`[InternalControlView] Control ${currentControlId}: natureFromRcm=${natureFromRcm}, attributes.Nature=${attributes.Nature}, final nature=${nature}, isAutomated=${isAutomatedControl}`);
            
            // Only update if controlId hasn't changed during load
            if (loadedControlIdRef.current === currentControlId) {
              setIsAutomated(isAutomatedControl);
              
              if (isAutomatedControl) {
                // Load automated data - map from saved JSON structure
                setAutomatedData((prev) => ({
                  ...prev,
                  controlId: details.ControlId || prev.controlId,
                  controlName: details.ControlName || prev.controlName,
                  workspaceLinked: details.WorkspaceLinked || prev.workspaceLinked,
                  controlDescription: details.ControlDescription || prev.controlDescription,
                  typeOfControl: attributes.TypeOfControl || prev.typeOfControl,
                  nature: "Automated" as const,
                  approach: attributes.Approach || prev.approach,
                  controlType: attributes.ControlType || prev.controlType,
                  testOperatingEffectiveness: summary.TailoringQuestions?.TestOperatingEffectiveness ?? prev.testOperatingEffectiveness,
                  relatedAccounts: summary.RelatedAccountsAndAreas?.RelatedAccounts || prev.relatedAccounts,
                  // EvaluateDesign automated fields
                  gitcControlsOverAccess: controlData.EvaluateDesign?.DesignImplementation?.GitcControlsOverAccess || prev.gitcControlsOverAccess,
                  reliesOnSystemConfiguration: controlData.EvaluateDesign?.DesignImplementation?.ReliesOnSystemConfiguration || prev.reliesOnSystemConfiguration,
                  configurationSettings: controlData.EvaluateDesign?.DesignImplementation?.ConfigurationSettings || prev.configurationSettings,
                  managementIntendedDesign: controlData.EvaluateDesign?.DesignImplementation?.ManagementIntendedDesign || prev.managementIntendedDesign,
                  accessToModifyConfiguration: controlData.EvaluateDesign?.DesignImplementation?.AccessToModifyConfiguration || prev.accessToModifyConfiguration,
                  configurationChangeFrequency: controlData.EvaluateDesign?.DesignImplementation?.ConfigurationChangeFrequency || prev.configurationChangeFrequency,
                  proceduresToUnderstandDesign: controlData.EvaluateDesign?.DesignImplementation?.ProceduresToUnderstandDesign || prev.proceduresToUnderstandDesign,
                  gitcControlsOverSystemConfig: controlData.EvaluateDesign?.DesignImplementation?.GitcControlsOverSystemConfig || prev.gitcControlsOverSystemConfig,
                  hasVariations: controlData.EvaluateDesign?.DesignImplementation?.HasVariations || prev.hasVariations,
                  proceduresToUnderstandVariants: controlData.EvaluateDesign?.DesignImplementation?.ProceduresToUnderstandVariants || prev.proceduresToUnderstandVariants,
                  additionalProcedures: controlData.EvaluateDesign?.DesignImplementation?.AdditionalProcedures || prev.additionalProcedures,
                  variants: controlData.EvaluateDesign?.DesignImplementation?.Variants || prev.variants,
                  sourceData: controlData.EvaluateDesign?.SourceData || prev.sourceData,
                  designFactor1Response: controlData.EvaluateDesign?.DesignFactors?.Factor1Response || prev.designFactor1Response,
                  designFactor2Enabled: controlData.EvaluateDesign?.DesignFactors?.Factor2Enabled ?? prev.designFactor2Enabled,
                  controlOwners: controlData.EvaluateDesign?.DesignFactors?.ControlOwners || prev.controlOwners,
                  designFactor3Enabled: controlData.EvaluateDesign?.DesignFactors?.Factor3Enabled ?? prev.designFactor3Enabled,
                  frequency: controlData.EvaluateDesign?.DesignFactors?.Frequency || prev.frequency,
                  consistency: controlData.EvaluateDesign?.DesignFactors?.Consistency || prev.consistency,
                  designFactor4Enabled: controlData.EvaluateDesign?.DesignFactors?.Factor4Enabled ?? prev.designFactor4Enabled,
                  designFactor4Content: controlData.EvaluateDesign?.DesignFactors?.Factor4Content || prev.designFactor4Content,
                  designFactor5Enabled: controlData.EvaluateDesign?.DesignFactors?.Factor5Enabled ?? prev.designFactor5Enabled,
                  designFactor5Content: controlData.EvaluateDesign?.DesignFactors?.Factor5Content || prev.designFactor5Content,
                  isDependentOnOtherControls: controlData.EvaluateDesign?.DependentControls?.IsDependent ?? prev.isDependentOnOtherControls,
                  dependentControls: controlData.EvaluateDesign?.DependentControls?.Controls || prev.dependentControls,
                  isDesignEffective: controlData.EvaluateDesign?.Effectiveness?.IsDesignEffective || prev.isDesignEffective,
                  isProperlyImplemented: controlData.EvaluateDesign?.Effectiveness?.IsProperlyImplemented || prev.isProperlyImplemented,
                  riskAssociated: controlData.RiskAssociated?.RiskAssociated || prev.riskAssociated,
                  riskBasisConclusion: controlData.RiskAssociated?.RiskBasisConclusion || prev.riskBasisConclusion,
                  timingOfProcedure: "Conclude at Interim" as const,
                  riskOfMaterialMisstatement: controlData.OperatingEffectiveness?.TimingAndExtent?.RiskOfMaterialMisstatement || prev.riskOfMaterialMisstatement,
                  riskAssociatedWithControl: controlData.OperatingEffectiveness?.TimingAndExtent?.RiskAssociatedWithControl || prev.riskAssociatedWithControl,
                  sampleSize: controlData.OperatingEffectiveness?.TimingAndExtent?.SampleSize || prev.sampleSize,
                  sampleSizeRationale: controlData.OperatingEffectiveness?.TimingAndExtent?.SampleSizeRationale || prev.sampleSizeRationale,
                  usingWorkOfOthers: controlData.OperatingEffectiveness?.TimingAndExtent?.UsingWorkOfOthers || prev.usingWorkOfOthers,
                  workOfOthersApproach: controlData.OperatingEffectiveness?.TimingAndExtent?.WorkOfOthersApproach || prev.workOfOthersApproach,
                  isInformationUsed: controlData.OperatingEffectiveness?.TimingAndExtent?.IsInformationUsed || prev.isInformationUsed,
                  operatingProcedures: controlData.OperatingEffectiveness?.NatureOfProcedures?.Procedures || prev.operatingProcedures,
                  isOperatingEffective: controlData.OperatingEffectiveness?.Conclusion?.IsOperatingEffective || prev.isOperatingEffective,
                  rollforwardFromDate: controlData.Rollforward?.RollforwardPeriod?.FromDate || prev.rollforwardFromDate,
                  rollforwardToDate: controlData.Rollforward?.RollforwardPeriod?.ToDate || prev.rollforwardToDate,
                  rollforwardInquiries: controlData.Rollforward?.RollforwardInquiry?.Inquiries || prev.rollforwardInquiries,
                  hasIcfrChanges: controlData.Rollforward?.RollforwardInquiry?.HasIcfrChanges ?? prev.hasIcfrChanges,
                  icfrChanges: controlData.Rollforward?.RollforwardInquiry?.IcfrChanges || prev.icfrChanges,
                  additionalProceduresRollforward: controlData.Rollforward?.RollforwardInquiry?.AdditionalProcedures || prev.additionalProceduresRollforward,
                  gitcOperatingEffectively: controlData.Rollforward?.RollforwardInquiry?.GitcOperatingEffectively ?? prev.gitcOperatingEffectively,
                  gitcDetails: controlData.Rollforward?.RollforwardInquiry?.GitcDetails || prev.gitcDetails,
                  isInquirySufficient: controlData.Rollforward?.RollforwardInquiry?.IsInquirySufficient ?? prev.isInquirySufficient,
                  rollforwardSteps: controlData.Rollforward?.RollforwardInquiry?.Steps || prev.rollforwardSteps,
                  rollforwardOperatingEffective: controlData.Rollforward?.Conclusion?.RollforwardOperatingEffective || prev.rollforwardOperatingEffective,
                  rollforwardOperatingTestsDocuments: controlData.Rollforward?.RollforwardOperatingTests?.Documents || prev.rollforwardOperatingTestsDocuments || [],
                  rollforwardOperatingTestsTextResponse: controlData.Rollforward?.RollforwardOperatingTests?.TextResponse || prev.rollforwardOperatingTestsTextResponse,
                }));
                isInitialLoadCompleteRef.current = true;
              } else {
                // Load manual data
                setManualData((prev) => ({
                  ...prev,
                  controlId: details.ControlId || prev.controlId,
                  controlName: details.ControlName || prev.controlName,
                  workspaceLinked: details.WorkspaceLinked || prev.workspaceLinked,
                  controlDescription: details.ControlDescription || prev.controlDescription,
                  typeOfControl: attributes.TypeOfControl || prev.typeOfControl,
                  nature: attributes.Nature || prev.nature,
                  approach: attributes.Approach || prev.approach,
                  controlTypes: attributes.ControlTypes || prev.controlTypes,
                  addressesSignificantRisk: summary.TailoringQuestions?.AddressesSignificantRisk ?? prev.addressesSignificantRisk,
                  includesReviewElement: summary.TailoringQuestions?.IncludesReviewElement ?? prev.includesReviewElement,
                  detailedDescriptionEnabled: summary.DetailedDescription?.Enabled ?? prev.detailedDescriptionEnabled,
                  steps: summary.DetailedDescription?.Steps || prev.steps,
                  iucAssociations: summary.DetailedDescription?.IUCAssociations || prev.iucAssociations,
                  iucDocuments: summary.DetailedDescription?.IUCDocuments || prev.iucDocuments || [],
                  iucTextResponse: summary.DetailedDescription?.IUCTextResponse || prev.iucTextResponse,
                  controlOutputs: summary.DetailedDescription?.ControlOutputs || prev.controlOutputs,
                  outputDocuments: summary.DetailedDescription?.OutputDocuments || prev.outputDocuments || [],
                  outputTextResponse: summary.DetailedDescription?.OutputTextResponse || prev.outputTextResponse,
                  relatedAccounts: summary.RelatedAccountsAndAreas?.RelatedAccounts || prev.relatedAccounts,
                  designImplementationSteps: controlData.EvaluateDesign?.DesignImplementation?.Steps || prev.designImplementationSteps,
                  testOfDesignImplementation: controlData.EvaluateDesign?.DesignImplementation?.TestOfDesignImplementation || prev.testOfDesignImplementation,
                  evidenceDocuments: controlData.EvaluateDesign?.DesignImplementation?.EvidenceDocuments || prev.evidenceDocuments,
                  evidenceDesignDocuments: controlData.EvaluateDesign?.DesignImplementation?.EvidenceDesignDocuments || prev.evidenceDesignDocuments || [],
                  evidenceDesignTextResponse: controlData.EvaluateDesign?.DesignImplementation?.EvidenceDesignTextResponse || prev.evidenceDesignTextResponse,
                  designFactor1Response: controlData.EvaluateDesign?.DesignFactors?.Factor1Response || prev.designFactor1Response,
                  designFactor2Enabled: controlData.EvaluateDesign?.DesignFactors?.Factor2Enabled ?? prev.designFactor2Enabled,
                  controlOwners: controlData.EvaluateDesign?.DesignFactors?.ControlOwners || prev.controlOwners,
                  designFactor3Enabled: controlData.EvaluateDesign?.DesignFactors?.Factor3Enabled ?? prev.designFactor3Enabled,
                  frequency: controlData.EvaluateDesign?.DesignFactors?.Frequency || prev.frequency,
                  consistency: controlData.EvaluateDesign?.DesignFactors?.Consistency || prev.consistency,
                  designFactor4Enabled: controlData.EvaluateDesign?.DesignFactors?.Factor4Enabled ?? prev.designFactor4Enabled,
                  designFactor4Content: controlData.EvaluateDesign?.DesignFactors?.Factor4Content || prev.designFactor4Content,
                  designFactor5Enabled: controlData.EvaluateDesign?.DesignFactors?.Factor5Enabled ?? prev.designFactor5Enabled,
                  designFactor5Content: controlData.EvaluateDesign?.DesignFactors?.Factor5Content || prev.designFactor5Content,
                  isDependentOnOtherControls: controlData.EvaluateDesign?.DependentControls?.IsDependent ?? prev.isDependentOnOtherControls,
                  dependentControls: controlData.EvaluateDesign?.DependentControls?.Controls || prev.dependentControls,
                  isDesignEffective: controlData.EvaluateDesign?.Effectiveness?.IsDesignEffective || prev.isDesignEffective,
                  isProperlyImplemented: controlData.EvaluateDesign?.Effectiveness?.IsProperlyImplemented || prev.isProperlyImplemented,
                  usePriorYearEvidence: controlData.PriorYearAuditEvidence?.UsePriorYearEvidence ?? prev.usePriorYearEvidence,
                  changesInProcess: controlData.PriorYearAuditEvidence?.ChangesInProcess || prev.changesInProcess,
                  changesSignificant: controlData.PriorYearAuditEvidence?.ChangesSignificant ?? prev.changesSignificant,
                  testedCurrentYear: controlData.PriorYearAuditEvidence?.TestedCurrentYear ?? prev.testedCurrentYear,
                  associatedControls: controlData.PriorYearAuditEvidence?.AssociatedControls || prev.associatedControls || [],
                  priorYearDocument: controlData.PriorYearAuditEvidence?.PriorYearDocument || prev.priorYearDocument,
                  riskAssociated: controlData.RiskAssociated?.RiskAssociated || prev.riskAssociated,
                  riskBasisConclusion: controlData.RiskAssociated?.RiskBasisConclusion || prev.riskBasisConclusion,
                  timingOfProcedure: controlData.OperatingEffectiveness?.TimingAndExtent?.TimingOfProcedure || prev.timingOfProcedure,
                  timingDocuments: controlData.OperatingEffectiveness?.TimingAndExtent?.TimingDocuments || prev.timingDocuments || [],
                  timingTextResponse: controlData.OperatingEffectiveness?.TimingAndExtent?.TimingTextResponse || prev.timingTextResponse,
                  frequencyOfPerformance: controlData.OperatingEffectiveness?.TimingAndExtent?.FrequencyOfPerformance || prev.frequencyOfPerformance,
                  riskOfMaterialMisstatement: controlData.OperatingEffectiveness?.TimingAndExtent?.RiskOfMaterialMisstatement || prev.riskOfMaterialMisstatement,
                  sampleSize: controlData.OperatingEffectiveness?.TimingAndExtent?.SampleSize || prev.sampleSize,
                  planningForDeviations: controlData.OperatingEffectiveness?.TimingAndExtent?.PlanningForDeviations ?? prev.planningForDeviations,
                  deviationDescription: controlData.OperatingEffectiveness?.TimingAndExtent?.DeviationDescription || prev.deviationDescription,
                  deviationRationale: controlData.OperatingEffectiveness?.TimingAndExtent?.DeviationRationale || prev.deviationRationale,
                  operatingSteps: controlData.OperatingEffectiveness?.NatureOfProcedures?.Steps || prev.operatingSteps,
                  isOperatingEffective: controlData.OperatingEffectiveness?.Conclusion?.IsOperatingEffective || prev.isOperatingEffective,
                  operatingTestsDocuments: controlData.OperatingEffectiveness?.OperatingTests?.Documents || prev.operatingTestsDocuments || [],
                  operatingTestsTextResponse: controlData.OperatingEffectiveness?.OperatingTests?.TextResponse || prev.operatingTestsTextResponse,
                  rollforwardFromDate: controlData.Rollforward?.RollforwardPeriod?.FromDate || prev.rollforwardFromDate,
                  rollforwardToDate: controlData.Rollforward?.RollforwardPeriod?.ToDate || prev.rollforwardToDate,
                  rollforwardInquiries: controlData.Rollforward?.RollforwardInquiry?.Inquiries || prev.rollforwardInquiries,
                  hasIcfrChanges: controlData.Rollforward?.RollforwardInquiry?.HasIcfrChanges ?? prev.hasIcfrChanges,
                  rollforwardOperatingTestsDocuments: controlData.Rollforward?.RollforwardOperatingTests?.Documents || prev.rollforwardOperatingTestsDocuments || [],
                  rollforwardOperatingTestsTextResponse: controlData.Rollforward?.RollforwardOperatingTests?.TextResponse || prev.rollforwardOperatingTestsTextResponse,
                  icfrChanges: controlData.Rollforward?.RollforwardInquiry?.IcfrChanges || prev.icfrChanges,
                  additionalProcedures: controlData.Rollforward?.RollforwardInquiry?.AdditionalProcedures || prev.additionalProcedures,
                  gitcOperatingEffectively: controlData.Rollforward?.RollforwardInquiry?.GitcOperatingEffectively ?? prev.gitcOperatingEffectively,
                  gitcDetails: controlData.Rollforward?.RollforwardInquiry?.GitcDetails || prev.gitcDetails,
                  isInquirySufficient: controlData.Rollforward?.RollforwardInquiry?.IsInquirySufficient ?? prev.isInquirySufficient,
                  rollforwardSteps: controlData.Rollforward?.RollforwardInquiry?.Steps || prev.rollforwardSteps,
                  rollforwardOperatingEffective: controlData.Rollforward?.Conclusion?.RollforwardOperatingEffective || prev.rollforwardOperatingEffective,
                }));
                isInitialLoadCompleteRef.current = true;
              }
            } else {
              // No existing data found - check Nature from RCM to determine template
              console.log(`[InternalControlView] No saved data found for ${currentControlId}, checking natureFromRcm: ${natureFromRcm}`);
              if (loadedControlIdRef.current === currentControlId) {
                const isAutomatedControl = natureFromRcm === "Automated";
                console.log(`[InternalControlView] Setting isAutomated to ${isAutomatedControl} for ${currentControlId} (natureFromRcm: ${natureFromRcm})`);
                setIsAutomated(isAutomatedControl);
                isInitialLoadCompleteRef.current = true;
              }
            }
          } else {
            // API not available or download failed - still check natureFromRcm
            console.log(`[InternalControlView] downloadClientJson API not available or failed, checking natureFromRcm: ${natureFromRcm}`);
            if (loadedControlIdRef.current === currentControlId) {
              const isAutomatedControl = natureFromRcm === "Automated";
              console.log(`[InternalControlView] Setting isAutomated to ${isAutomatedControl} for ${currentControlId} (natureFromRcm: ${natureFromRcm})`);
              setIsAutomated(isAutomatedControl);
              isInitialLoadCompleteRef.current = true;
            }
          }
        } else {
          // downloadClientJson API not available - still check natureFromRcm
          console.log(`[InternalControlView] downloadClientJson API not available, checking natureFromRcm: ${natureFromRcm}`);
          if (loadedControlIdRef.current === currentControlId) {
            const isAutomatedControl = natureFromRcm === "Automated";
            console.log(`[InternalControlView] Setting isAutomated to ${isAutomatedControl} for ${currentControlId} (natureFromRcm: ${natureFromRcm})`);
            setIsAutomated(isAutomatedControl);
            isInitialLoadCompleteRef.current = true;
          }
        }
      } catch (error) {
        console.error("Failed to load existing control data:", error);
        // Even on error, try to set isAutomated based on natureFromRcm
        if (loadedControlIdRef.current === currentControlId && natureFromRcm !== null) {
          const isAutomatedControl = natureFromRcm === "Automated";
          console.log(`[InternalControlView] Error occurred, but setting isAutomated to ${isAutomatedControl} based on natureFromRcm: ${natureFromRcm}`);
          setIsAutomated(isAutomatedControl);
        }
        isInitialLoadCompleteRef.current = true;
      } finally {
        // Only reset loading flag if controlId hasn't changed
        if (loadedControlIdRef.current === currentControlId) {
          isLoadingRef.current = false;
        }
      }
    };

    loadData();
  }, [controlId]);

  const handleSave = async () => {
    if (!controlId) {
      setSaveStatus({
        type: "error",
        message: "Control ID is required to save",
      });
      return;
    }

    setSaving(true);
    setSaveStatus({ type: null, message: "" });

    try {
      let jsonData: any;

      if (isAutomated) {
        // Save automated control data
        const data = automatedData;
        jsonData = {
          InternalControls: {
            Control: {
              ControlId: data.controlId,
              ControlSummary: {
                ControlDetails: {
                  ControlId: data.controlId,
                  ControlName: data.controlName,
                  WorkspaceLinked: data.workspaceLinked,
                  ControlDescription: data.controlDescription,
                },
                ControlAttributes: {
                  TypeOfControl: data.typeOfControl,
                  Nature: data.nature,
                  Approach: data.approach,
                  ControlType: data.controlType,
                },
                TailoringQuestions: {
                  TestOperatingEffectiveness: data.testOperatingEffectiveness,
                },
                RelatedAccountsAndAreas: {
                  RelatedAccounts: data.relatedAccounts || [],
                },
              },
              EvaluateDesign: {
                DesignImplementation: {
                  GitcControlsOverAccess: data.gitcControlsOverAccess,
                  ReliesOnSystemConfiguration: data.reliesOnSystemConfiguration,
                  ConfigurationSettings: data.configurationSettings,
                  ManagementIntendedDesign: data.managementIntendedDesign,
                  AccessToModifyConfiguration: data.accessToModifyConfiguration,
                  ConfigurationChangeFrequency: data.configurationChangeFrequency,
                  ProceduresToUnderstandDesign: data.proceduresToUnderstandDesign,
                  GitcControlsOverSystemConfig: data.gitcControlsOverSystemConfig,
                  HasVariations: data.hasVariations,
                  ProceduresToUnderstandVariants: data.proceduresToUnderstandVariants,
                  AdditionalProcedures: data.additionalProcedures,
                  Variants: data.variants,
                },
                SourceData: data.sourceData,
                DesignFactors: {
                  Factor1Response: data.designFactor1Response || "",
                  Factor2Enabled: data.designFactor2Enabled,
                  ControlOwners: data.controlOwners,
                  Factor3Enabled: data.designFactor3Enabled,
                  Frequency: data.frequency,
                  Consistency: data.consistency,
                  Factor4Enabled: data.designFactor4Enabled,
                  Factor4Content: data.designFactor4Content,
                  Factor5Enabled: data.designFactor5Enabled,
                  Factor5Content: data.designFactor5Content,
                },
                DependentControls: {
                  IsDependent: data.isDependentOnOtherControls,
                  Controls: data.dependentControls,
                },
                Effectiveness: {
                  IsDesignEffective: data.isDesignEffective,
                  IsProperlyImplemented: data.isProperlyImplemented,
                },
              },
              RiskAssociated: {
                RiskAssociated: data.riskAssociated,
                RiskBasisConclusion: data.riskBasisConclusion,
              },
              OperatingEffectiveness: {
                TimingAndExtent: {
                  TimingOfProcedure: data.timingOfProcedure,
                  RiskOfMaterialMisstatement: data.riskOfMaterialMisstatement,
                  RiskAssociatedWithControl: data.riskAssociatedWithControl,
                  SampleSize: data.sampleSize,
                  SampleSizeRationale: data.sampleSizeRationale,
                  UsingWorkOfOthers: data.usingWorkOfOthers,
                  WorkOfOthersApproach: data.workOfOthersApproach,
                  IsInformationUsed: data.isInformationUsed,
                },
                NatureOfProcedures: {
                  Procedures: data.operatingProcedures,
                },
                Conclusion: {
                  IsOperatingEffective: data.isOperatingEffective,
                },
              },
              Rollforward: {
                RollforwardPeriod: {
                  FromDate: data.rollforwardFromDate,
                  ToDate: data.rollforwardToDate,
                },
                RollforwardInquiry: {
                  Inquiries: data.rollforwardInquiries,
                  HasIcfrChanges: data.hasIcfrChanges,
                  IcfrChanges: data.icfrChanges,
                  AdditionalProcedures: data.additionalProceduresRollforward,
                  GitcOperatingEffectively: data.gitcOperatingEffectively,
                  GitcDetails: data.gitcDetails,
                  IsInquirySufficient: data.isInquirySufficient,
                  Steps: data.rollforwardSteps,
                },
                Conclusion: {
                  RollforwardOperatingEffective: data.rollforwardOperatingEffective,
                },
              },
            },
          },
        };
      } else {
        // Save manual control data
        const data = manualData;
        jsonData = {
          InternalControls: {
            Control: {
              ControlId: data.controlId,
              ControlSummary: {
                ControlDetails: {
                  ControlId: data.controlId,
                  ControlName: data.controlName,
                  WorkspaceLinked: data.workspaceLinked,
                  ControlDescription: data.controlDescription,
                },
                ControlAttributes: {
                  TypeOfControl: data.typeOfControl,
                  Nature: data.nature,
                  Approach: data.approach,
                  ControlTypes: data.controlTypes,
                },
                TailoringQuestions: {
                  AddressesSignificantRisk: data.addressesSignificantRisk,
                  IncludesReviewElement: data.includesReviewElement,
                },
                DetailedDescription: {
                  Enabled: data.detailedDescriptionEnabled,
                  Steps: data.steps,
                  IUCAssociations: data.iucAssociations,
                  IUCDocuments: data.iucDocuments || [],
                  IUCTextResponse: data.iucTextResponse,
                  ControlOutputs: data.controlOutputs,
                  OutputDocuments: data.outputDocuments || [],
                  OutputTextResponse: data.outputTextResponse,
                },
                RelatedAccountsAndAreas: {
                  RelatedAccounts: data.relatedAccounts || [],
                },
              },
              EvaluateDesign: {
                DesignImplementation: {
                  Steps: data.designImplementationSteps,
                  TestOfDesignImplementation: data.testOfDesignImplementation,
                  EvidenceDocuments: data.evidenceDocuments,
                  EvidenceDesignDocuments: data.evidenceDesignDocuments || [],
                  EvidenceDesignTextResponse: data.evidenceDesignTextResponse,
                },
                DesignFactors: {
                  Factor1Response: data.designFactor1Response || "",
                  Factor2Enabled: data.designFactor2Enabled,
                  ControlOwners: data.controlOwners,
                  Factor3Enabled: data.designFactor3Enabled,
                  Frequency: data.frequency,
                  Consistency: data.consistency,
                  Factor4Enabled: data.designFactor4Enabled,
                  Factor4Content: data.designFactor4Content,
                  Factor5Enabled: data.designFactor5Enabled,
                  Factor5Content: data.designFactor5Content,
                },
                DependentControls: {
                  IsDependent: data.isDependentOnOtherControls,
                  Controls: data.dependentControls,
                },
                Effectiveness: {
                  IsDesignEffective: data.isDesignEffective,
                  IsProperlyImplemented: data.isProperlyImplemented,
                },
              },
              PriorYearAuditEvidence: {
                UsePriorYearEvidence: data.usePriorYearEvidence,
                ChangesInProcess: data.changesInProcess,
                ChangesSignificant: data.changesSignificant,
                TestedCurrentYear: data.testedCurrentYear,
                AssociatedControls: data.associatedControls || [],
                PriorYearDocument: data.priorYearDocument,
                PriorYearDocuments: data.priorYearDocuments || [],
                PriorYearTextResponse: data.priorYearTextResponse,
              },
              RiskAssociated: {
                RiskAssociated: data.riskAssociated,
                RiskBasisConclusion: data.riskBasisConclusion,
              },
              OperatingEffectiveness: {
                TimingAndExtent: {
                  TimingOfProcedure: data.timingOfProcedure,
                  TimingDocuments: data.timingDocuments || [],
                  TimingTextResponse: data.timingTextResponse,
                  FrequencyOfPerformance: data.frequencyOfPerformance,
                  RiskOfMaterialMisstatement: data.riskOfMaterialMisstatement,
                  SampleSize: data.sampleSize,
                  PlanningForDeviations: data.planningForDeviations,
                  DeviationDescription: data.deviationDescription,
                  DeviationRationale: data.deviationRationale,
                },
                NatureOfProcedures: {
                  Steps: data.operatingSteps,
                },
                OperatingTests: {
                  Documents: data.operatingTestsDocuments || [],
                  TextResponse: data.operatingTestsTextResponse,
                },
                Conclusion: {
                  IsOperatingEffective: data.isOperatingEffective,
                },
              },
              Rollforward: {
                RollforwardPeriod: {
                  FromDate: data.rollforwardFromDate,
                  ToDate: data.rollforwardToDate,
                },
                RollforwardInquiry: {
                  Inquiries: data.rollforwardInquiries,
                  HasIcfrChanges: data.hasIcfrChanges,
                  IcfrChanges: data.icfrChanges,
                  AdditionalProcedures: data.additionalProcedures,
                  GitcOperatingEffectively: data.gitcOperatingEffectively,
                  GitcDetails: data.gitcDetails,
                  IsInquirySufficient: data.isInquirySufficient,
                  Steps: data.rollforwardSteps,
                },
                RollforwardOperatingTests: {
                  Documents: data.rollforwardOperatingTestsDocuments || [],
                  TextResponse: data.rollforwardOperatingTestsTextResponse,
                },
                Conclusion: {
                  RollforwardOperatingEffective: data.rollforwardOperatingEffective,
                },
              },
            },
          },
        };
      }

      const fileName = `Libraries_InternalControlResponses_${controlId}.json`;
      const jsonContent = JSON.stringify(jsonData, null, 2);
      const base64Content = btoa(unescape(encodeURIComponent(jsonContent)));

      if (window.sharePointAPI && typeof window.sharePointAPI.uploadFile === "function") {
        const result = await window.sharePointAPI.uploadFile({
          fileContent: base64Content,
          fileName: fileName,
          folderName: "juggernaut",
          sectionList: ["InternalControlResponses"],
        });

        if (result.success) {
          // Also update Libraries_RCM.json with control_name and workspace
          try {
            if (window.sharePointAPI && typeof window.sharePointAPI.downloadRcmJson === "function" && typeof window.sharePointAPI.uploadRcmJson === "function") {
              const rcmResult = await window.sharePointAPI.downloadRcmJson();
              
              if (rcmResult.success) {
                const rcmData = rcmResult.data || { libraries_RCM: {} };
                const librariesRcm = rcmData.libraries_RCM || rcmData.libraries_rcm || {};
                
                // Get current data (either automated or manual)
                const currentData = isAutomated ? automatedData : manualData;
                
                // Update or create the entry for this control ID
                if (!librariesRcm[controlId]) {
                  librariesRcm[controlId] = {
                    romm: [],
                    control_name: "",
                    workspace: [],
                    TypeOfControl: "Direct",
                    Nature: currentData.nature || "Manual",
                  };
                }
                
                // Update control_name and workspace
                librariesRcm[controlId].control_name = currentData.controlName || "";
                librariesRcm[controlId].workspace = currentData.workspaceLinked || [];
                
                // Preserve other fields if they exist
                if (!librariesRcm[controlId].TypeOfControl) {
                  librariesRcm[controlId].TypeOfControl = currentData.typeOfControl || "Direct";
                }
                if (!librariesRcm[controlId].Nature) {
                  librariesRcm[controlId].Nature = currentData.nature || "Manual";
                }
                if (!librariesRcm[controlId].romm) {
                  librariesRcm[controlId].romm = [];
                }
                
                // Update the structure
                const updatedRcmData = {
                  libraries_RCM: librariesRcm,
                };
                
                // Upload the updated RCM JSON
                const uploadRcmResult = await window.sharePointAPI.uploadRcmJson(updatedRcmData);
                
                if (!uploadRcmResult.success) {
                  console.error("Failed to update Libraries_RCM.json:", uploadRcmResult.error);
                  // Don't fail the entire save operation, just log the error
                }
              } else {
                console.error("Failed to download Libraries_RCM.json:", rcmResult.error);
                // Don't fail the entire save operation, just log the error
              }
            }
          } catch (rcmError) {
            console.error("Error updating Libraries_RCM.json:", rcmError);
            // Don't fail the entire save operation, just log the error
          }
          
          setSaveStatus({
            type: "success",
            message: "Control data saved successfully!",
          });
          setTimeout(() => {
            setSaveStatus({ type: null, message: "" });
          }, 3000);
        } else {
          throw new Error(result.error || "Failed to save");
        }
      } else {
        throw new Error("SharePoint API not available");
      }
    } catch (error) {
      console.error("Failed to save control data:", error);
      setSaveStatus({
        type: "error",
        message: error instanceof Error ? error.message : "Failed to save control data",
      });
    } finally {
      setSaving(false);
    }
  };

  const data = isAutomated ? automatedData : manualData;
  const handleDataChange = isAutomated ? handleAutomatedDataChange : handleManualDataChange;

  const sections = [
    {
      id: "control-summary" as const,
      label: "Control Summary",
      number: 1,
      visible: true,
    },
    {
      id: "evaluate-design" as const,
      label: "Evaluate Design and Determine Implementation",
      number: 2,
      visible: true,
    },
    ...(isAutomated ? [] : [{
      id: "prior-year" as const,
      label: "Prior Year Audit Evidence",
      number: 3,
      visible: true,
    }]),
    {
      id: "risk-associated" as const,
      label: "Risk Associated with Control",
      number: isAutomated ? 3 : 4,
      visible: isAutomated ? automatedData.testOperatingEffectiveness : !manualData.usePriorYearEvidence,
    },
    {
      id: "operating-effectiveness" as const,
      label: "Operating Effectiveness",
      number: isAutomated ? 4 : 5,
      visible: isAutomated ? automatedData.testOperatingEffectiveness : !manualData.usePriorYearEvidence,
    },
    {
      id: "rollforward" as const,
      label: "Rollforward",
      number: isAutomated ? 5 : 6,
      visible: isAutomated ? automatedData.testOperatingEffectiveness : (!manualData.usePriorYearEvidence && manualData.timingOfProcedure === "Conclude at Interim"),
    },
  ];

  const visibleSections = sections.filter((s) => s.visible);

  const handleOpenReviewComment = (section: SectionType) => {
    setReviewCommentSection(section);
    setReviewCommentOpen(true);
  };

  const getQuestionId = (section: SectionType): string => {
    if (!controlId) return "";
    return `${controlId}_${section}`;
  };

  const getSectionLabel = (section: SectionType): string => {
    const sectionInfo = sections.find(s => s.id === section);
    return sectionInfo?.label || section;
  };

  const renderSection = () => {
    if (isAutomated) {
      switch (activeSection) {
        case "control-summary":
          return <ControlSummaryAutomated data={automatedData} onChange={handleAutomatedDataChange} controlId={controlId} />;
        case "evaluate-design":
          return <EvaluateDesignAutomated data={automatedData} onChange={handleAutomatedDataChange} controlId={controlId} />;
        case "risk-associated":
          return <RiskAssociatedAutomated data={automatedData} onChange={handleAutomatedDataChange} />;
        case "operating-effectiveness":
          return <OperatingEffectivenessAutomated data={automatedData} onChange={handleAutomatedDataChange} controlId={controlId} />;
        case "rollforward":
          return <RollforwardAutomated data={automatedData} onChange={handleAutomatedDataChange} />;
        default:
          return <ControlSummaryAutomated data={automatedData} onChange={handleAutomatedDataChange} controlId={controlId} />;
      }
    } else {
      switch (activeSection) {
        case "control-summary":
          return <ControlSummary data={manualData} onChange={handleManualDataChange} controlId={controlId} />;
        case "evaluate-design":
          return <EvaluateDesign data={manualData} onChange={handleManualDataChange} controlId={controlId} />;
        case "prior-year":
          return <PriorYearAudit data={manualData} onChange={handleManualDataChange} />;
        case "risk-associated":
          return <RiskAssociated data={manualData} onChange={handleManualDataChange} />;
        case "operating-effectiveness":
          return <OperatingEffectiveness data={manualData} onChange={handleManualDataChange} controlId={controlId} />;
        case "rollforward":
          return <Rollforward data={manualData} onChange={handleManualDataChange} />;
        default:
          return <ControlSummary data={manualData} onChange={handleManualDataChange} controlId={controlId} />;
      }
    }
  };

  return (
    <div className="w-full h-full overflow-y-auto pb-8">
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">{controlName || "Internal Control"}</h1>
            {controlId && (
              <p className="text-sm text-white/60 mt-1">Control ID: {controlId}</p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {saveStatus.type && (
              <div
                className={`px-4 py-2 rounded-md text-sm ${
                  saveStatus.type === "success"
                    ? "bg-green-500/20 text-green-400 border border-green-500/30"
                    : "bg-red-500/20 text-red-400 border border-red-500/30"
                }`}
              >
                {saveStatus.message}
              </div>
            )}
            <Button
              onClick={handleSave}
              disabled={saving || !controlId}
              className="bg-[#4da3ff] hover:bg-[#4da3ff]/80 text-white"
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Section Header with Review Comment Button */}
        <div className="mb-4 flex items-center justify-between border-b border-white/10 pb-3">
          <h2 className="text-xl font-semibold text-white">
            {getSectionLabel(activeSection)}
          </h2>
          {controlId && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleOpenReviewComment(activeSection)}
              className="text-[#fbbf24] hover:text-[#fbbf24]/80 hover:bg-white/10"
              title="Review Comments"
            >
              <MessageSquare className="h-4 w-4 mr-2" />
              Review Comments
            </Button>
          )}
        </div>
        
        {/* Render the active section */}
        {renderSection()}

        {/* Review Comment Modal */}
        {reviewCommentSection && controlId && (
          <ReviewComment
            questionId={getQuestionId(reviewCommentSection)}
            questionText={getSectionLabel(reviewCommentSection)}
            isOpen={reviewCommentOpen}
            onClose={() => {
              setReviewCommentOpen(false);
              setReviewCommentSection(null);
            }}
            workspace="Control"
            subPhase={controlName || controlId}
          />
        )}
      </div>
    </div>
  );
};

